import React, { useEffect, useMemo, useState } from "react";
import { Pagination } from "flowbite-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  AlertTriangle,
  Eye,
  Filter,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
} from "lucide-react";
import AdminLayout, { StatCard } from "./AdminLayout";
import AdminFormModal from "./AdminFormModal";
import { errorMessage, toastOpts } from "./adminApi";
import { getUserRole } from "../../utils/auth";

/**
 * Generic "view / create / update" screen for reference data.
 *
 * Props:
 *  - title, subtitle, icon, entityName ("product")
 *  - queryKey: react-query key for the list
 *  - fetchAll(): Promise<row[]>
 *  - createItem(values): Promise            (optional -> no create button)
 *  - updateItem(id, values, row): Promise   (optional -> no edit button)
 *  - getId(row)
 *  - columns: [{ key, label, render?(row), className? }]
 *  - searchKeys: string[] | searchFn(row, term)
 *  - fields: field descriptors for AdminFormModal
 *  - toFormValues(row): initial values for edit / view (defaults to the row)
 *  - filters: [{ name, label, options|optionsLoader, match(row, value) }]
 *  - pageSize (default 10)
 *  - extraStats: [{ icon, value(rows), label, tone }]
 */
export default function AdminCrudPage({
  title,
  subtitle,
  icon: Icon,
  entityName,
  queryKey,
  fetchAll,
  createItem,
  updateItem,
  getId = (row) => row.id,
  columns,
  searchKeys = [],
  fields,
  toFormValues = defaultToFormValues,
  filters = [],
  pageSize = 10,
  extraStats = [],
  emptyHint,
}) {
  const queryClient = useQueryClient();
  const isAdmin = getUserRole() === "Admin";

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterValues, setFilterValues] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [modal, setModal] = useState({ open: false, mode: "create", row: null });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim().toLowerCase());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const { data: rows = [], isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: [queryKey],
    queryFn: fetchAll,
    staleTime: 30 * 1000,
    retry: 1,
  });

  const filtered = useMemo(() => {
    let list = rows;
    if (debouncedSearch) {
      list = list.filter((row) => {
        if (typeof searchKeys === "function") return searchKeys(row, debouncedSearch);
        return searchKeys.some((k) => {
          const v = getPath(row, k);
          return v !== undefined && v !== null && String(v).toLowerCase().includes(debouncedSearch);
        });
      });
    }
    filters.forEach((f) => {
      const v = filterValues[f.name];
      if (v !== undefined && v !== "") list = list.filter((row) => f.match(row, v));
    });
    return list;
  }, [rows, debouncedSearch, searchKeys, filters, filterValues]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const pageRows = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  const openCreate = () => setModal({ open: true, mode: "create", row: null });
  const openEdit = (row) => setModal({ open: true, mode: "edit", row });
  const openView = (row) => setModal({ open: true, mode: "view", row });
  const closeModal = () => setModal((m) => ({ ...m, open: false }));

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      if (modal.mode === "create") {
        await createItem(values);
        toast.success(`${capitalize(entityName)} created successfully.`, toastOpts);
      } else {
        await updateItem(getId(modal.row), values, modal.row);
        toast.success(`${capitalize(entityName)} updated successfully.`, toastOpts);
      }
      closeModal();
      queryClient.invalidateQueries({ queryKey: [queryKey] });
    } catch (err) {
      toast.error(errorMessage(err), toastOpts);
    } finally {
      setIsSubmitting(false);
    }
  };

  const initialValues = useMemo(
    () => (modal.row ? toFormValues(modal.row) : {}),
    [modal.row, toFormValues]
  );

  const canCreate = isAdmin && typeof createItem === "function";
  const canEdit = isAdmin && typeof updateItem === "function";

  return (
    <AdminLayout
      title={title}
      subtitle={subtitle}
      icon={Icon}
      actions={
        <>
          <button
            onClick={() => refetch()}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-cyan-700 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </button>
          {canCreate && (
            <button
              onClick={openCreate}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 transition-colors shadow-sm"
            >
              <Plus className="w-4 h-4" />
              New {capitalize(entityName)}
            </button>
          )}
        </>
      }
    >
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Icon} value={rows.length} label={`Total ${pluralize(entityName)}`} />
        {(debouncedSearch || Object.values(filterValues).some((v) => v)) && (
          <StatCard icon={Filter} value={filtered.length} label="Matching filters" tone="gray" />
        )}
        {extraStats.map((s) => (
          <StatCard key={s.label} icon={s.icon} value={s.value(rows)} label={s.label} tone={s.tone} />
        ))}
        {isAdmin ? (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <div className="text-amber-500 mb-3">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <p className="text-sm font-semibold text-gray-700">Admin Access</p>
            <p className="text-xs font-medium mt-1 text-gray-500">You can add and update {pluralize(entityName)}</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <div className="text-gray-400 mb-3">
              <Eye className="w-5 h-5" />
            </div>
            <p className="text-sm font-semibold text-gray-700">Read-only Access</p>
            <p className="text-xs font-medium mt-1 text-gray-500">Only administrators can make changes</p>
          </div>
        )}
      </div>

      {/* Search + filters */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={`Search ${pluralize(entityName)}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
            />
          </div>
          {filters.map((f) => (
            <FilterSelect
              key={f.name}
              filter={f}
              value={filterValues[f.name] || ""}
              onChange={(v) => {
                setFilterValues((fv) => ({ ...fv, [f.name]: v }));
                setCurrentPage(1);
              }}
            />
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-24 text-gray-400">
            <Loader2 className="w-10 h-10 animate-spin text-cyan-500 mb-3" />
            <p className="text-sm">Loading {pluralize(entityName)}...</p>
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-24 text-gray-400">
            <AlertTriangle className="w-10 h-10 text-red-400 mb-3" />
            <p className="text-sm font-medium text-red-600">Failed to load {pluralize(entityName)}</p>
            <p className="text-xs mt-1 text-gray-500">{errorMessage(error)}</p>
            <button onClick={() => refetch()} className="mt-3 text-sm text-cyan-600 hover:underline">
              Try again
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-gray-400">
            {Icon && <Icon className="w-10 h-10 mb-3" />}
            <p className="text-sm font-medium">No {pluralize(entityName)} found</p>
            {debouncedSearch ? (
              <p className="text-xs mt-1">Try adjusting your search</p>
            ) : (
              emptyHint && <p className="text-xs mt-1">{emptyHint}</p>
            )}
            {canCreate && !debouncedSearch && (
              <button onClick={openCreate} className="mt-4 flex items-center gap-2 text-sm text-cyan-600 hover:underline">
                <Plus className="w-4 h-4" /> Add the first {entityName}
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">#</th>
                    {columns.map((c) => (
                      <th
                        key={c.key}
                        className={`px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide ${c.className || ""}`}
                      >
                        {c.label}
                      </th>
                    ))}
                    <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pageRows.map((row, index) => (
                    <tr key={getId(row) ?? index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-4 text-gray-400 text-xs font-mono">
                        {(safePage - 1) * pageSize + index + 1}
                      </td>
                      {columns.map((c) => (
                        <td key={c.key} className={`px-5 py-4 text-gray-700 ${c.cellClassName || ""}`}>
                          {c.render ? c.render(row) : displayValue(getPath(row, c.key))}
                        </td>
                      ))}
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openView(row)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                            title="View details"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            View
                          </button>
                          {canEdit && (
                            <button
                              onClick={() => openEdit(row)}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-lg hover:bg-cyan-100 transition-colors"
                              title="Edit"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                              Edit
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-between px-5 py-4 border-t border-gray-100 gap-3">
              <p className="text-sm text-gray-500">
                Showing{" "}
                <span className="font-medium text-gray-700">
                  {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, filtered.length)}
                </span>{" "}
                of <span className="font-medium text-gray-700">{filtered.length}</span> {pluralize(entityName)}
              </p>
              {totalPages > 1 && (
                <Pagination
                  currentPage={safePage}
                  totalPages={totalPages}
                  onPageChange={(p) => setCurrentPage(p)}
                  showIcons
                  theme={{
                    pages: {
                      selector: {
                        active: "bg-cyan-600 text-white border-cyan-600 hover:bg-cyan-700",
                      },
                    },
                  }}
                />
              )}
            </div>
          </>
        )}
      </div>

      <AdminFormModal
        open={modal.open}
        mode={modal.mode}
        icon={Icon}
        title={
          modal.mode === "create"
            ? `New ${capitalize(entityName)}`
            : modal.mode === "edit"
            ? `Edit ${capitalize(entityName)}`
            : `${capitalize(entityName)} Details`
        }
        subtitle={subtitle}
        fields={fields}
        initialValues={initialValues}
        onClose={closeModal}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
      />
    </AdminLayout>
  );
}

function FilterSelect({ filter, value, onChange }) {
  const { data: loaded = [], isLoading } = useQuery({
    queryKey: ["filterOptions", filter.name],
    queryFn: filter.optionsLoader,
    enabled: Boolean(filter.optionsLoader),
    staleTime: 5 * 60 * 1000,
  });
  const options = filter.optionsLoader ? loaded : filter.options || [];
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={isLoading}
      className="lg:w-64 px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none bg-white"
    >
      <option value="">{isLoading ? "Loading..." : `All ${filter.label}`}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

// ── helpers ────────────────────────────────────────────────────────────────
/** Stable default so `initialValues` is not recomputed on every parent render. */
const defaultToFormValues = (row) => row;

export function getPath(obj, path) {
  if (!obj || !path) return undefined;
  return String(path)
    .split(".")
    .reduce((acc, k) => (acc === undefined || acc === null ? undefined : acc[k]), obj);
}

export function displayValue(v) {
  if (v === undefined || v === null || v === "") return <span className="text-gray-400">—</span>;
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
}

export function capitalize(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

export function pluralize(s) {
  if (!s) return s;
  if (s.endsWith("y") && !/[aeiou]y$/.test(s)) return s.slice(0, -1) + "ies";
  if (s.endsWith("s")) return s + "es";
  return s + "s";
}
